import { memo, useMemo } from 'react'
import { ChevronLeft, ChevronRight, ChevronsUp, ImageOff, Type } from 'lucide-react'
import type { Announcement } from '../../lib/infoserviceApi.js'
import { announcementsApi } from '../../lib/infoserviceApi.js'
import { dayColumns, dayState, monthSpans } from '../../lib/announcementGrid.js'

interface AnnouncementsTableProps {
  announcements: Announcement[]
  from: string
  days: number
  today: string
  selectedId: string | null
  canManage: boolean
  onSelect: (id: string) => void
  onToggleDay: (announcement: Announcement, day: string) => void
  onShift: (deltaDays: number) => void
  onToday: () => void
}

/**
 * La feuille « Infographies », en plus lisible (maquette `annonces-v2`) :
 * identité figée à gauche sur une seule ligne, puis un petit carré arrondi par
 * jour. Seuls les jours cochés se voient ; une case vide reste une trace
 * discrète, rendue visible au survol de la ligne et sur la ligne ouverte, pour
 * qu'on lise des plages de diffusion plutôt qu'un quadrillage.
 */
export const AnnouncementsTable = memo(function AnnouncementsTable({
  announcements, from, days, today, selectedId, canManage, onSelect, onToggleDay, onShift, onToday,
}: AnnouncementsTableProps) {
  const columns = useMemo(() => dayColumns(from, days, today), [from, days, today])
  const months = useMemo(() => monthSpans(columns), [columns])
  const todayLabel = useMemo(() => {
    let offset = 0
    for (const month of months) {
      if (columns.slice(offset, offset + month.span).some(column => column.today)) return month.label
      offset += month.span
    }
    return null
  }, [columns, months])

  return (
    <div className="announcements-table-wrap">
      <div className="announcements-table__scroll">
        <table className="announcements-table">
          <thead>
            <tr className="announcements-table__band">
              <th className="announcements-table__lead" colSpan={3} scope="colgroup">
                <span className="announcements-table__count">
                  {announcements.length} annonce{announcements.length > 1 ? 's' : ''}
                </span>
                <span className="announcements-table__nav">
                  <button type="button" className="announcements-table__nav-button" onClick={() => onShift(-28)} aria-label="Quatre semaines plus tôt"><ChevronLeft size={14} aria-hidden="true" /></button>
                  <button type="button" className="announcements-table__nav-today" onClick={onToday}>Aujourd’hui</button>
                  <button type="button" className="announcements-table__nav-button" onClick={() => onShift(28)} aria-label="Quatre semaines plus tard"><ChevronRight size={14} aria-hidden="true" /></button>
                </span>
              </th>
              {months.map(month => (
                <th
                  key={month.label}
                  colSpan={month.span}
                  scope="colgroup"
                  className={`announcements-table__month${month.label === todayLabel ? ' is-current' : ''}`}
                >
                  {month.label.split(' ')[0]}
                </th>
              ))}
            </tr>
            <tr className="announcements-table__heads">
              <th scope="col" className="announcements-table__col announcements-table__col--identity">Organisme / événement</th>
              <th scope="col" className="announcements-table__col announcements-table__col--text">Texte</th>
              <th scope="col" className="announcements-table__col announcements-table__col--prio">Prio</th>
              {columns.map(column => (
                <th
                  key={column.day}
                  scope="col"
                  className={[
                    'announcements-table__day-head',
                    column.weekdayLetter === 'D' ? 'is-sunday' : '',
                    column.today ? 'is-today' : '',
                    column.monthStart ? 'is-month-start' : '',
                  ].filter(Boolean).join(' ')}
                  aria-label={column.day}
                >
                  <span>{column.dayOfMonth}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {announcements.map(announcement => (
              <AnnouncementRow
                key={announcement.id}
                announcement={announcement}
                columns={columns}
                today={today}
                selected={announcement.id === selectedId}
                canManage={canManage}
                onSelect={onSelect}
                onToggleDay={onToggleDay}
              />
            ))}
          </tbody>
        </table>
      </div>
      <Legend />
    </div>
  )
})

interface RowProps {
  announcement: Announcement
  columns: ReturnType<typeof dayColumns>
  today: string
  selected: boolean
  canManage: boolean
  onSelect: (id: string) => void
  onToggleDay: (announcement: Announcement, day: string) => void
}

const AnnouncementRow = memo(function AnnouncementRow({ announcement, columns, today, selected, canManage, onSelect, onToggleDay }: RowProps) {
  const checked = useMemo(() => new Set(announcement.days), [announcement.days])
  const notes = useMemo(() => new Map(announcement.dayNotes.map(note => [note.day, note.text])), [announcement.dayNotes])
  const archived = announcement.archivedAt !== null
  const onToday = checked.has(today) && !archived && !announcement.cancelled
  const flags = [
    onToday && !announcement.imageAvailable ? 'is-blocked' : '',
    announcement.cancelled ? 'is-cancelled' : '',
    selected ? 'is-selected' : '',
  ].filter(Boolean).join(' ')

  return (
    <tr className={`announcements-table__row ${flags}`}>
      <th scope="row" className="announcements-table__col announcements-table__col--identity">
        <button type="button" className="announcements-table__open" onClick={() => onSelect(announcement.id)} aria-pressed={selected}>
          {announcement.totalDays === 0 && (
            <span className="announcements-table__no-days" role="img" aria-label="Aucun jour de diffusion" title="Aucun jour de diffusion" />
          )}
          <span className="announcements-table__thumb" aria-hidden="true">
            {announcement.imageAvailable
              ? <img src={announcementsApi.imageUrl(announcement.id, announcement.updatedAt, 'thumb')} alt="" width={40} height={24} loading="lazy" decoding="async" />
              : <ImageOff size={13} />}
          </span>
          <span className="announcements-table__names">
            <span className="announcements-table__organization">{announcement.organizationLabel}</span>
            {announcement.eventLabel && <span className="announcements-table__event">{announcement.eventLabel}</span>}
          </span>
          {!announcement.imageAvailable && <span className="announcements-table__flag is-warning" title="Sans image : ne passera pas">Sans image</span>}
          {announcement.cancelled && <span className="announcements-table__flag is-danger">Annulée</span>}
        </button>
      </th>
      <td className="announcements-table__col announcements-table__col--text">
        {announcement.hasText && <Type size={13} className="announcements-table__icon" aria-label="L’image contient du texte" />}
      </td>
      <td className="announcements-table__col announcements-table__col--prio">
        {announcement.priority && <span className="announcements-table__badge is-priority" title="Prioritaire"><ChevronsUp size={11} aria-label="Prioritaire" /></span>}
      </td>
      {columns.map(column => {
        const isChecked = checked.has(column.day)
        const state = dayState({ checked: isChecked, imageAvailable: announcement.imageAvailable, cancelled: announcement.cancelled, archived })
        const note = notes.get(column.day)
        return (
          <td
            key={column.day}
            className={[
              'announcements-table__day',
              column.today ? 'is-today' : '',
              column.monthStart ? 'is-month-start' : '',
            ].filter(Boolean).join(' ')}
          >
            <button
              type="button"
              className={`announcements-table__square is-${state}${isChecked && column.today ? ' is-today' : ''}${note ? ' has-note' : ''}`}
              aria-pressed={isChecked}
              aria-label={`${announcement.organizationLabel}, ${column.day} : ${isChecked ? 'diffusée' : 'non diffusée'}${note ? ` — ${note}` : ''}`}
              title={note ?? undefined}
              disabled={!canManage || archived || announcement.sheetLinked}
              onClick={() => onToggleDay(announcement, column.day)}
            />
          </td>
        )
      })}
    </tr>
  )
})

const Legend = memo(function Legend() {
  return (
    <footer className="announcements-legend" aria-label="Légende">
      <span><span className="announcements-legend__swatch is-onAir" aria-hidden="true" /> Jour de diffusion, image reliée</span>
      <span><span className="announcements-legend__swatch is-missingImage" aria-hidden="true" /> Cochée sans image — ne passe pas</span>
      <span><span className="announcements-legend__swatch is-noDays" aria-hidden="true" /> Aucun jour de diffusion</span>
      <span><Type size={13} className="announcements-table__icon" aria-hidden="true" /> L’image contient du texte</span>
      <span><span className="announcements-table__badge is-priority" aria-hidden="true"><ChevronsUp size={11} /></span> Prioritaire — en tête de chaque tour</span>
    </footer>
  )
})
